import React, { useEffect, useState } from 'react';
import { auth } from '../firebase/firebase';
import { getIdToken } from 'firebase/auth';
import { doc, getDoc, updateDoc, getFirestore } from 'firebase/firestore';
import './PostLoginPopup.css';

const db = getFirestore();

const PostLoginPopup = ({ email, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);

  // ✅ Check if user is already subscribed
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const currentUser = auth.currentUser;
        if (!currentUser) return;

        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const data = userDoc.data();

        if (data?.subscribed) {
          setAlreadySubscribed(true);
        }
      } catch (err) {
        console.error('❌ Error checking subscription:', err.message);
      }
    };

    const dismissed = localStorage.getItem('popup-dismissed');
    if (dismissed === 'true') {
      onClose(); // Close permanently if skipped earlier
    } else {
      checkSubscription(); // Check status from Firestore
    }
  }, [onClose]);

  // ✅ Subscribe handler
  const handleSubscribe = async () => {
    if (alreadySubscribed) {
      setMessage('ℹ️ You are already subscribed.');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('No logged in user');

      const token = await getIdToken(currentUser, true);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/save-subscriber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Subscription failed');

      await updateDoc(doc(db, 'users', currentUser.uid), {
        subscribed: true,
      });

      localStorage.setItem('popup-dismissed', 'true');
      setAlreadySubscribed(true);
      setMessage(`✅ ${email} has been subscribed successfully!`);
    } catch (err) {
      console.error('❌ Subscription error:', err.message);
      setMessage('❌ Subscription failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Skip forever (dismiss popup permanently)
  const handleSkip = () => {
    localStorage.setItem('popup-dismissed', 'true');
    onClose();
  };

  // ✅ Soft close (don’t dismiss permanently)
  const handleCloseOnce = () => {
    onClose(); // just close for this session
  };

  return (
    <div className="popup-overlay">
      <div className="popup-box">
        <h4 className="text-center">📩 Subscribe for Daily Job Alerts</h4>
        <p className="text-center mb-1">Your Email:</p>
        <div className="email-display text-center mb-3"><b>{email}</b></div>

        <div className="d-grid gap-2">
          <button
            className="btn btn-primary"
            onClick={handleSubscribe}
            disabled={loading || alreadySubscribed}
          >
            {loading ? 'Subscribing...' : alreadySubscribed ? 'Already Subscribed' : 'Subscribe'}
          </button>

          <button className="btn btn-outline-secondary" onClick={handleCloseOnce}>
            Close & Continue (Show Next Time)
          </button>

          <button className="btn btn-secondary" onClick={handleSkip}>
            Skip Forever
          </button>
        </div>

        {message && (
          <p className="mt-3 text-center fw-semibold">{message}</p>
        )}
      </div>
    </div>
  );
};

export default PostLoginPopup;
